import { Component, OnInit, signal, inject, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AdminService, DashboardStats, RecentAppointment } from '../../../services/admin.service';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

@Component({
	selector: 'app-dashboard',
	imports: [CommonModule],
	templateUrl: './dashboard.html',
	styleUrl: './dashboard.css'
})
export class Dashboard implements OnInit, AfterViewInit {
	@ViewChild('trendChart') trendChartRef!: ElementRef<HTMLCanvasElement>;
	@ViewChild('statusChart') statusChartRef!: ElementRef<HTMLCanvasElement>;
	
	private trendChart?: Chart;
	private statusChart?: Chart;
	private adminService = inject(AdminService);
	private router = inject(Router);
	
	selectedPeriod = signal<'today' | '2days' | '7days' | 'month' | '6months'>('6months');
	isLoading = signal(true);
	dashboardData = signal<DashboardStats | null>(null);
	
	// Chart data for status distribution
	statusChartData = signal<{label: string, value: number, color: string, percentage: string}[]>([]);
	
	// Trend chart data
	trendChartData = signal<{label: string, value: number}[]>([]);

	// Dynamic stats that will be loaded from backend
	stats = signal([
		{
			label: 'Total Appointments',
			value: signal('0'),
			change: signal('0%'),
			trend: signal<'up' | 'down'>('up'),
			icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
			color: '#5d202d',
			colorDark: '#3d141d',
			key: 'appointments_this_month'
		},
		{
			label: 'Pending Bookings',
			value: signal('0'),
			change: signal('0%'),
			trend: signal<'up' | 'down'>('up'),
			icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
			color: '#caa78a',
			colorDark: '#a88567',
			key: 'pending_appointments'
		},
		{
			label: 'Total Clients',
			value: signal('0'),
			change: signal('0%'),
			trend: signal<'up' | 'down'>('up'),
			icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
			color: '#8c6a4f',
			colorDark: '#6d5138',
			key: 'total_customers'
		},
		{
			label: 'Active Services',
			value: signal('0'),
			change: signal('0%'),
			trend: signal<'up' | 'down'>('up'),
			icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
			color: '#3d3d3d',
			colorDark: '#2d2d2d',
			key: 'active_services'
		}
	]);

	recentAppointments = signal<any[]>([]);

	ngOnInit() {
		this.loadDashboardStats();
		this.loadRecentAppointments();
	}

	ngAfterViewInit() {
		this.loadTrendData();
	}

	onPeriodChange(period: 'today' | '2days' | '7days' | 'month' | '6months') {
		this.selectedPeriod.set(period);
		this.loadTrendData();
	}

	loadDashboardStats() {
		this.isLoading.set(true);
		
		this.adminService.getAppointmentStats().subscribe({
			next: (response) => {
				if (response.status && response.data) {
					this.dashboardData.set(response.data);
					this.updateStatsFromData(response.data);
					this.updateChartData(response.data);
				}
				this.isLoading.set(false);
			},
			error: (error) => {
				console.error('Error loading dashboard stats:', error);
				this.isLoading.set(false);
				// Optionally show error notification to user
			}
		});
	}

	updateChartData(data: DashboardStats) {
		const total = (data.pending_appointments ?? 0) + (data.confirmed_appointments ?? 0) + 
		              (data.completed_appointments ?? 0) + (data.cancelled_appointments ?? 0);
		
		if (total > 0) {
			this.statusChartData.set([
				{ 
					label: 'Pending', 
					value: data.pending_appointments ?? 0, 
					color: '#f59e0b',
					percentage: ((data.pending_appointments ?? 0) / total * 100).toFixed(1)
				},
				{ 
					label: 'Confirmed', 
					value: data.confirmed_appointments ?? 0, 
					color: '#10b981',
					percentage: ((data.confirmed_appointments ?? 0) / total * 100).toFixed(1)
				},
				{ 
					label: 'Completed', 
					value: data.completed_appointments ?? 0, 
					color: '#3b82f6',
					percentage: ((data.completed_appointments ?? 0) / total * 100).toFixed(1)
				},
				{ 
					label: 'Cancelled', 
					value: data.cancelled_appointments ?? 0, 
					color: '#ef4444',
					percentage: ((data.cancelled_appointments ?? 0) / total * 100).toFixed(1)
				}
			]);
			
			// Create the donut chart after data is set
			setTimeout(() => this.createStatusChart(), 100);
		}
	}

	createStatusChart() {
		if (!this.statusChartRef) return;

		// Destroy existing chart
		if (this.statusChart) {
			this.statusChart.destroy();
		}

		const data = this.statusChartData();
		const ctx = this.statusChartRef.nativeElement.getContext('2d');
		if (!ctx) return;

		this.statusChart = new Chart(ctx, {
			type: 'doughnut',
			data: {
				labels: data.map(d => d.label),
				datasets: [{
					data: data.map(d => d.value),
					backgroundColor: data.map(d => d.color),
					borderColor: '#ffffff',
					borderWidth: 3,
					hoverOffset: 10
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: '65%',
				plugins: {
					legend: {
						display: false
					},
					tooltip: {
						backgroundColor: 'rgba(0, 0, 0, 0.8)',
						padding: 12,
						titleColor: '#fff',
						bodyColor: '#fff',
						borderColor: '#5d202d',
						borderWidth: 1,
						callbacks: {
							label: (context) => {
								const label = context.label || '';
								const value = context.parsed;
								const percentage = data[context.dataIndex].percentage;
								return `${label}: ${value} (${percentage}%)`;
							}
						}
					}
				}
			}
		});
	}

	updateStatsFromData(data: DashboardStats) {
		// Safely get values with defaults
		const appointmentsThisMonth = data.appointments_this_month ?? 0;
		const appointmentsLastMonth = data.appointments_last_month ?? 0;
		const customersThisMonth = data.customers_this_month ?? 0;
		const customersLastMonth = data.customers_last_month ?? 0;
		const pendingAppointments = data.pending_appointments ?? 0;
		const totalCustomers = data.total_customers ?? 0;
		const activeServices = data.active_services ?? 0;

		// Calculate percentage changes
		const appointmentChange = this.calculateChange(appointmentsThisMonth, appointmentsLastMonth);
		const customerChange = this.calculateChange(customersThisMonth, customersLastMonth);

		// Update Total Appointments
		this.stats()[0].value.set(appointmentsThisMonth.toString());
		if (appointmentChange !== 0) {
			this.stats()[0].change.set(`${appointmentChange > 0 ? '+' : ''}${appointmentChange.toFixed(1)}%`);
		}
		this.stats()[0].trend.set(appointmentChange >= 0 ? 'up' : 'down');

		// Update Pending Bookings
		this.stats()[1].value.set(pendingAppointments.toString());
		this.stats()[1].change.set(''); // No historical comparison for pending
		this.stats()[1].trend.set('up');

		// Update Total Clients
		this.stats()[2].value.set(totalCustomers.toString());
		if (customerChange !== 0) {
			this.stats()[2].change.set(`${customerChange > 0 ? '+' : ''}${customerChange.toFixed(1)}%`);
		}
		this.stats()[2].trend.set(customerChange >= 0 ? 'up' : 'down');

		// Update Active Services
		this.stats()[3].value.set(activeServices.toString());
		this.stats()[3].change.set(''); // No historical comparison for services
		this.stats()[3].trend.set('up');
	}

	calculateChange(current: number, previous: number): number {
		if (previous === 0) return current > 0 ? 100 : 0;
		return ((current - previous) / previous) * 100;
	}

	loadTrendData() {
		const period = this.selectedPeriod();
		
		this.adminService.getTrendData(period).subscribe({
			next: (response) => {
				if (response.status && response.data) {
					this.trendChartData.set(response.data);
					this.createTrendChart();
				}
			},
			error: (error) => {
				console.error('Error loading trend data:', error);
				this.trendChartData.set([]);
			}
		});
	}

	createTrendChart() {
		if (!this.trendChartRef) return;

		// Destroy existing chart
		if (this.trendChart) {
			this.trendChart.destroy();
		}

		const data = this.trendChartData();
		const ctx = this.trendChartRef.nativeElement.getContext('2d');
		if (!ctx) return;

		this.trendChart = new Chart(ctx, {
			type: 'bar',
			data: {
				labels: data.map(d => d.label),
				datasets: [{
					label: 'Appointments',
					data: data.map(d => d.value),
					backgroundColor: 'rgba(93, 32, 45, 0.8)',
					borderColor: 'rgba(93, 32, 45, 1)',
					borderWidth: 2,
					borderRadius: 8,
					hoverBackgroundColor: 'rgba(202, 167, 138, 0.8)',
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: false
					},
					tooltip: {
						backgroundColor: 'rgba(0, 0, 0, 0.8)',
						padding: 12,
						titleColor: '#fff',
						bodyColor: '#fff',
						borderColor: '#5d202d',
						borderWidth: 1,
						callbacks: {
							label: (context) => `${context.parsed.y} appointments`
						}
					}
				},
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							stepSize: 1,
							color: '#6b7280'
						},
						grid: {
							color: 'rgba(0, 0, 0, 0.05)'
						}
					},
					x: {
						ticks: {
							color: '#6b7280'
						},
						grid: {
							display: false
						}
					}
				}
			}
		});
	}

	loadRecentAppointments() {
		this.adminService.getAppointments({ limit: 5 }).subscribe({
			next: (response) => {
				if (response.status && response.data) {
					// Handle both paginated and non-paginated responses
					const appointmentsList = response.data.data || response.data;
					
					if (Array.isArray(appointmentsList)) {
						const appointments = appointmentsList.map(apt => ({
							id: apt.id,
							name: apt.customer ? `${apt.customer.first_name} ${apt.customer.last_name}` : 'Unknown',
							initials: apt.customer ? this.getInitials(apt.customer.first_name, apt.customer.last_name) : '??',
							service: apt.service?.service_name || 'No service',
							date: this.formatDate(apt.appointment_date),
							time: this.formatTime(apt.start_time),
							status: apt.status
						}));
						this.recentAppointments.set(appointments);
					}
				}
			},
			error: (error) => {
				console.error('Error loading recent appointments:', error);
			}
		});
	}

	getInitials(firstName: string, lastName: string): string {
		return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
	}

	formatDate(dateString: string): string {
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
	}

	formatTime(timeString: string): string {
		const [hours, minutes] = timeString.split(':');
		const hour = parseInt(hours);
		const ampm = hour >= 12 ? 'PM' : 'AM';
		const displayHour = hour % 12 || 12;
		return `${displayHour}:${minutes} ${ampm}`;
	}

	// Quick Actions Navigation
	navigateToAddService() {
		this.router.navigate(['/admin/services/add']);
	}

	navigateToAppointments() {
		this.router.navigate(['/admin/appointments']);
	}

	navigateToReports() {
		// For now, navigate to dashboard - can be changed later
		this.router.navigate(['/admin/dashboard']);
	}

	navigateToSettings() {
		this.router.navigate(['/admin/site-settings']);
	}

	// Helper method to calculate total appointments for the chart
	getTotalAppointments(): number {
		return this.statusChartData().reduce((sum, item) => sum + item.value, 0);
	}

	// Calculate circle progress for monthly comparison (circumference = 2 * PI * radius = 502.65)
	getCircleProgress(thisMonth: number, lastMonth: number): string {
		const circumference = 502.65; // 2 * Math.PI * 80
		const maxValue = Math.max(thisMonth, lastMonth, 1); // Avoid division by zero
		const percentage = (thisMonth / maxValue) * 100;
		const dashLength = (circumference * percentage) / 100;
		return `${dashLength} ${circumference}`;
	}

	// Calculate donut segment for status distribution
	getDonutSegment(percentage: string): string {
		const circumference = 502.65; // 2 * Math.PI * 80
		const dashLength = (circumference * parseFloat(percentage)) / 100;
		return `${dashLength} ${circumference}`;
	}

	// Calculate offset for donut segments to stack them
	getDonutOffset(index: number): number {
		const circumference = 502.65;
		let offset = 0;
		
		for (let i = 0; i < index; i++) {
			const prevPercentage = parseFloat(this.statusChartData()[i].percentage);
			offset -= (circumference * prevPercentage) / 100;
		}
		
		return offset;
	}

	// Get max value for chart scaling
	getMaxChartValue(): number {
		const values = this.trendChartData().map(d => d.value);
		const max = Math.max(...values, 1);
		// Round up to nearest 10 for better visualization
		return Math.ceil(max / 10) * 10;
	}

	// Calculate bar height percentage
	getBarHeight(value: number): number {
		const max = this.getMaxChartValue();
		return (value / max) * 100;
	}

	// Get total appointments in trend data
	getTrendTotal(): number {
		return this.trendChartData().reduce((sum, item) => sum + item.value, 0);
	}

	// Get average appointments in trend data
	getTrendAverage(): number {
		const data = this.trendChartData();
		if (data.length === 0) return 0;
		return Math.round(this.getTrendTotal() / data.length);
	}
}

